import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  gradeSubmission,
  listSubmissions,
  getEligibleQuestions,
  type Submission,
  type EligibleQuestion,
} from "../../services/companies-api";

/** FR-013: the CO Member's queue of submissions with ungraded free-text answers — list, then
 * mark each free-text answer correct/incorrect, which finalizes the submission once every
 * free-text answer on it has been graded. */
export function GradingPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [questions, setQuestions] = useState<EligibleQuestion[]>([]);
  const [grading, setGrading] = useState<Record<string, boolean>>({});

  async function refresh() {
    if (!companyId) return;
    setSubmissions(await listSubmissions(companyId, "pending_grading"));
  }

  useEffect(() => {
    if (!companyId) return;
    refresh();
    getEligibleQuestions(companyId).then(setQuestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  function questionText(id: string) {
    return questions.find((q) => q.id === id)?.text ?? id;
  }

  async function handleGrade(submissionId: string, questionId: string, isCorrect: boolean) {
    if (!companyId) return;
    setGrading((g) => ({ ...g, [submissionId]: true }));
    try {
      await gradeSubmission(companyId, submissionId, [{ questionId, isCorrect }]);
      await refresh();
    } finally {
      setGrading((g) => ({ ...g, [submissionId]: false }));
    }
  }

  return (
    <div className="p-6">
      <button onClick={() => navigate(`/co-member/companies/${companyId}`)} className="mb-4 text-sm text-brand">
        ← Back to company
      </button>
      <h1 className="mb-4 font-sans text-xl text-ink">Grading Queue</h1>

      <div className="flex flex-col gap-6">
        {submissions.map((s) => (
          <div key={s.id} className="rounded border border-surface-alt p-4">
            <p className="mb-2 text-sm text-body">Submission {s.id}</p>
            {Object.entries(s.answers)
              .filter(([, a]) => a.isCorrect === null)
              .map(([questionId, a]) => (
                <div key={questionId} className="mb-3 border-b border-surface-alt pb-3 last:border-b-0">
                  <p className="mb-1 text-ink">{questionText(questionId)}</p>
                  <p className="mb-2 text-sm text-body">Answer: {Array.isArray(a.given) ? a.given.join(", ") : a.given}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleGrade(s.id, questionId, true)}
                      disabled={grading[s.id]}
                      className="rounded bg-brand px-3 py-1 text-sm text-surface"
                    >
                      Correct
                    </button>
                    <button
                      onClick={() => handleGrade(s.id, questionId, false)}
                      disabled={grading[s.id]}
                      className="rounded bg-surface-alt px-3 py-1 text-sm text-accent"
                    >
                      Incorrect
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ))}
        {submissions.length === 0 && <p className="text-body">Nothing pending grading right now.</p>}
      </div>
    </div>
  );
}
